/**
 * Enterprise dossier for a sell-side EOI, RFQ, or RFT. Fields live in meta_json.invitation_dossier.
 */
import React from 'react';
import { Alert, Card, Col, Form, Input, InputNumber, Row, Select, Space, Switch, Typography } from 'antd';
import {
  COMMERCIAL_EVALUATION_METHODS,
  CONTRACT_CATEGORY_OPTIONS,
  technicalWeightTotal,
  workflowStagesFor,
  type EpcInvitationDossier,
} from '@/constants/epcInvitationWorkflow';
import type { EpcInvitationKind } from '@/constants/epcInvitationKind';

const { Text } = Typography;
const { TextArea } = Input;

export const EpcInvitationDossierFields: React.FC<{ kind: EpcInvitationKind | 'PQ' }> = ({ kind }) => {
  const form = Form.useFormInstance();
  const rft = Form.useWatch(['dossier', 'rft'], form) as EpcInvitationDossier['rft'] | undefined;
  const stages = workflowStagesFor(kind);
  const weightTotal = rft ? technicalWeightTotal(rft) : 0;
  const showQualification = kind === 'EOI' || kind === 'PQ' || kind === 'RFT';
  const showCommercial = kind === 'RFQ' || kind === 'RFT';

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small" title="Workflow">
        <Text type="secondary">
          {stages.map((stage, index) => `${index + 1}. ${stage}`).join(' · ')}
        </Text>
        <Form.Item name={['dossier', 'stage']} label="Current stage" style={{ marginTop: 12 }}>
          <Select options={stages.map((value) => ({ value, label: value }))} />
        </Form.Item>
        <Form.Item name={['dossier', 'purpose']} label="Purpose">
          <TextArea rows={2} />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name={['dossier', 'businessUnit']} label="Business unit">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={['dossier', 'contractCategory']} label="Expected contract category">
              <Select
                allowClear
                options={CONTRACT_CATEGORY_OPTIONS.map((value) => ({ value, label: value }))}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card size="small" title="Ownership">
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name={['dossier', 'ownership', 'requestor']} label="Requestor / pursuit owner">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={['dossier', 'ownership', 'commercialOwner']} label="Commercial owner">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={['dossier', 'ownership', 'technicalReviewer']} label="Technical reviewer">
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={['dossier', 'ownership', 'approver']} label="Approver">
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {showQualification ? (
        <Card size="small" title="Qualification definition">
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Freeze certificates, experience, capacity, financial suitability, HSE, ISO and local compliance before responses are opened. Later changes need a formal amendment."
          />
          <Form.Item name={['dossier', 'qualification', 'certificates']} label="Mandatory certificates">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'qualification', 'experience']} label="Experience">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'qualification', 'capacity']} label="Capacity">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'qualification', 'financial']} label="Financial suitability">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'qualification', 'hse']} label="HSE">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'qualification', 'iso']} label="ISO and quality">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'qualification', 'localCompliance']} label="Local compliance">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name={['dossier', 'qualification', 'criteriaFrozen']}
            label="Criteria frozen"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Card>
      ) : null}

      {showCommercial ? (
        <Card size="small" title="Commercial conditions">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name={['dossier', 'commercialTerms', 'paymentTerms']} label="Payment terms">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['dossier', 'commercialTerms', 'incoterms']} label="Incoterms / delivery basis">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'commercialTerms', 'freight']} label="Freight">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'commercialTerms', 'vat']} label="VAT">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'commercialTerms', 'warranty']} label="Warranty">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      ) : null}

      {kind === 'RFT' ? (
        <Card size="small" title="Tender sections and evaluation">
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Commercial content stays closed until the technical qualification is approved. Ordinary screens do not bypass that gate."
          />
          <Form.Item name={['dossier', 'rft', 'particulars']} label="Tender particulars">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'sow']} label="Scope of work, deliverables and exclusions">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'boq']} label="BOQ / rate schedule">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'technicalSpecification']} label="Technical specification">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'qualification']} label="Qualification requirements">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'commercialConditions']} label="Commercial conditions">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'contractConditions']} label="Contract conditions">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'evaluationMethodology']} label="Evaluation methodology">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name={['dossier', 'rft', 'bidInstructions']} label="Bid instructions">
            <TextArea rows={2} />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name={['dossier', 'rft', 'commercialMethod']} label="Commercial method">
                <Select options={COMMERCIAL_EVALUATION_METHODS.map((value) => ({ value, label: value }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name={['dossier', 'rft', 'qualificationThreshold']} label="Qualification threshold (0–100)">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'rft', 'experienceWeight']} label="Experience %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'rft', 'methodologyWeight']} label="Methodology %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'rft', 'resourcesWeight']} label="Resources %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'rft', 'hseWeight']} label="HSE / quality %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name={['dossier', 'rft', 'deliveryWeight']} label="Delivery plan %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            type={weightTotal === 100 ? 'success' : 'warning'}
            showIcon
            style={{ marginBottom: 12 }}
            message={`Technical weights total ${weightTotal}%. Published evaluation weights must total 100%.`}
          />
          <Form.Item
            name={['dossier', 'rft', 'commercialLockedUntilTechnical']}
            label="Commercial proposal locked until technical qualification is approved"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Card>
      ) : null}
    </Space>
  );
};
